/**
 * FlahaSOIL — unified Special Note surface for the dark workspace.
 *
 * One component family for methodology caveats, estimate badges,
 * evidence limitations, agronomic guidance, and real constraints.
 * Prefer this over stock MUI Alerts on results and scientific charts.
 * Print/PDF report notes stay on ScientificDisclaimer (light paper).
 */
import type { ReactNode } from "react";
import {
	Box,
	Chip,
	Collapse,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useId, useState } from "react";

export type SpecialNoteKind =
	| "methodology"
	| "estimate"
	| "limitation"
	| "guidance"
	| "constraint";

export interface SpecialNoteProps {
	kind: SpecialNoteKind;
	/** Short human title (shown first). */
	title: string;
	/** Primary one-sentence body. */
	children: ReactNode;
	/** Optional deeper method / formula detail behind progressive disclosure. */
	detail?: ReactNode;
	/** Default expanded for detail (when detail is provided). */
	defaultExpanded?: boolean;
	/** Compact density for under-chart footnotes. */
	dense?: boolean;
	/** Optional test id. */
	"data-testid"?: string | undefined;
}

const KIND_META: Record<
	SpecialNoteKind,
	{
		label: string;
		icon: typeof InfoOutlinedIcon;
		accent: string;
		surface: string;
		border: string;
	}
> = {
	methodology: {
		label: "Methodology",
		icon: ScienceOutlinedIcon,
		accent: "workspace.blue",
		surface: "rgba(74, 156, 197, 0.1)",
		border: "rgba(74, 156, 197, 0.32)",
	},
	estimate: {
		label: "Model estimate",
		icon: BiotechOutlinedIcon,
		accent: "workspace.bronze",
		surface: "rgba(169, 119, 66, 0.12)",
		border: "rgba(169, 119, 66, 0.36)",
	},
	limitation: {
		label: "Data limitation",
		icon: WarningAmberOutlinedIcon,
		accent: "warning.main",
		surface: "rgba(216, 132, 0, 0.1)",
		border: "rgba(216, 132, 0, 0.34)",
	},
	guidance: {
		label: "In practice",
		icon: LightbulbOutlinedIcon,
		accent: "workspace.green",
		surface: "rgba(119, 169, 87, 0.1)",
		border: "rgba(119, 169, 87, 0.32)",
	},
	constraint: {
		label: "Constraint",
		icon: WarningAmberOutlinedIcon,
		accent: "error.main",
		surface: "rgba(180, 35, 24, 0.1)",
		border: "rgba(180, 35, 24, 0.36)",
	},
};

export function SpecialNote({
	kind,
	title,
	children,
	detail,
	defaultExpanded = false,
	dense = false,
	"data-testid": testId,
}: SpecialNoteProps) {
	const meta = KIND_META[kind];
	const Icon = meta.icon;
	const detailId = useId();
	const [expanded, setExpanded] = useState(defaultExpanded);
	const hasDetail = detail != null && detail !== false && detail !== "";

	return (
		<Box
			component="aside"
			data-testid={testId ?? `special-note-${kind}`}
			data-note-kind={kind}
			sx={{
				p: dense ? 1.25 : 1.75,
				borderRadius: 1.5,
				border: "1px solid",
				borderColor: meta.border,
				backgroundColor: meta.surface,
				backgroundImage:
					"linear-gradient(145deg, rgba(255,255,255,0.04), rgba(255,255,255,0) 55%)",
			}}
		>
			<Stack direction="row" spacing={1.25} alignItems="flex-start">
				<Box
					aria-hidden="true"
					sx={{
						color: meta.accent,
						display: "flex",
						mt: 0.15,
						flexShrink: 0,
					}}
				>
					<Icon fontSize={dense ? "small" : "medium"} />
				</Box>
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						flexWrap="wrap"
						useFlexGap
						sx={{ mb: 0.5 }}
					>
						<Chip
							size="small"
							label={meta.label}
							sx={{
								height: 22,
								fontWeight: 600,
								color: meta.accent,
								borderColor: meta.border,
								backgroundColor: "rgba(12, 16, 14, 0.35)",
							}}
							variant="outlined"
						/>
						<Typography
							component="h3"
							variant={dense ? "subtitle2" : "subtitle1"}
							sx={{ fontWeight: 700, lineHeight: 1.3 }}
						>
							{title}
						</Typography>
					</Stack>
					<Box
						sx={{
							color: "workspace.textSecondary",
							display: "block",
							lineHeight: 1.55,
							fontSize: dense ? "0.75rem" : "0.875rem",
						}}
					>
						{children}
					</Box>
					{hasDetail ? (
						<>
							<Box sx={{ mt: 0.75 }}>
								<IconButton
									size="small"
									onClick={() => setExpanded((v) => !v)}
									aria-expanded={expanded}
									aria-controls={detailId}
									sx={{
										color: "workspace.textMuted",
										borderRadius: 1,
										px: 0.75,
										py: 0.25,
										"&:hover": { color: "workspace.textPrimary" },
									}}
								>
									{expanded ? (
										<ExpandLessIcon fontSize="small" />
									) : (
										<ExpandMoreIcon fontSize="small" />
									)}
									<Typography
										component="span"
										variant="caption"
										sx={{ ml: 0.5, fontWeight: 600 }}
									>
										{expanded ? "Hide detail" : "Learn more"}
									</Typography>
								</IconButton>
							</Box>
							<Collapse in={expanded}>
								<Typography
									id={detailId}
									variant="caption"
									component="div"
									sx={{
										mt: 1,
										pt: 1,
										borderTop: "1px solid",
										borderColor: "workspace.border",
										color: "workspace.textMuted",
										lineHeight: 1.55,
										display: "block",
									}}
								>
									{detail}
								</Typography>
							</Collapse>
						</>
					) : null}
				</Box>
			</Stack>
		</Box>
	);
}
